/**
 * Platform-level business sub-category (e.g. Bakery, HVAC, Residential).
 * Counter provides businessSubCategoryId + guid; Firestore auto-generates docId.
 *
 * Read-only for tenants. Managed exclusively in the admin app by OrderUp team.
 * Linked to a parent BusinessType via businessTypeGuid.
 */

export interface IBusinessSubCategory {
  /** Platform counter ID (92000001+) */
  readonly businessSubCategoryId: number;
  /** UUID for external references */
  readonly guid: string;
  /** FK to parent BusinessType.guid */
  readonly businessTypeGuid: string;
  /** Display name (e.g. "Bakery", "HVAC") */
  readonly name: string;
  /** Whether this sub-category is active */
  readonly isActive: boolean;
  /** Display sort order */
  readonly position: number;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface IBusinessSubCategoryData {
  businessSubCategoryId: number;
  guid: string;
  businessTypeGuid: string;
  name: string;
  isActive?: boolean;
  position?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export class BusinessSubCategory implements IBusinessSubCategory {
  readonly businessSubCategoryId: number;
  readonly guid: string;
  readonly businessTypeGuid: string;
  readonly name: string;
  readonly isActive: boolean;
  readonly position: number;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(data: IBusinessSubCategoryData) {
    this.businessSubCategoryId = data.businessSubCategoryId;
    this.guid = data.guid;
    this.businessTypeGuid = data.businessTypeGuid;
    this.name = data.name;
    this.isActive = data.isActive ?? true;
    this.position = data.position ?? 0;
    this.createdAt = data.createdAt ?? new Date();
    this.updatedAt = data.updatedAt ?? new Date();
  }

  toJSON(): IBusinessSubCategory {
    return {
      businessSubCategoryId: this.businessSubCategoryId,
      guid: this.guid,
      businessTypeGuid: this.businessTypeGuid,
      name: this.name,
      isActive: this.isActive,
      position: this.position,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromJSON(data: Record<string, unknown>): BusinessSubCategory {
    const businessSubCategoryId =
      data.businessSubCategoryId != null ? Number(data.businessSubCategoryId) : 0;
    if (typeof data.guid !== "string")
      throw new Error("BusinessSubCategory.fromJSON: guid is missing.");
    if (typeof data.businessTypeGuid !== "string")
      throw new Error("BusinessSubCategory.fromJSON: businessTypeGuid is missing.");
    if (typeof data.name !== "string")
      throw new Error("BusinessSubCategory.fromJSON: name is missing.");

    const convertTimestamp = (timestamp: unknown): Date | undefined => {
      if (!timestamp) return undefined;
      if (timestamp instanceof Date) return timestamp;
      if (
        timestamp &&
        typeof (timestamp as { toDate?: () => Date }).toDate === "function"
      ) {
        return (timestamp as { toDate: () => Date }).toDate();
      }
      if (
        timestamp &&
        typeof (timestamp as { seconds?: number }).seconds === "number"
      ) {
        const t = timestamp as { seconds: number; nanoseconds?: number };
        return new Date(
          t.seconds * 1000 + (t.nanoseconds ?? 0) / 1_000_000,
        );
      }
      const parsed = new Date(timestamp as string | number);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    };

    return new BusinessSubCategory({
      businessSubCategoryId,
      guid: data.guid as string,
      businessTypeGuid: data.businessTypeGuid as string,
      name: data.name as string,
      isActive: data.isActive as boolean | undefined,
      position: data.position as number | undefined,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    });
  }
}
